import React, { forwardRef } from 'react';
import type { Node } from 'react';
import styled from '@emotion/styled';

import { useComponentTheme } from '../useComponentTheme';
import { withSpacingStyles } from '../withSpacingStyles';

type Props = {
  align?: 'center' | 'left' | 'right',
  boxShadowDisplay?: 'always' | 'desktop' | 'never',
  children: Node,
  className: ?string,
  isHidden: boolean,
  mode?: string,
};

/**
 * ### Example
 * ```
 default {
    ...
    LayoutCard: {
      default: {
        backgroundColor: theme.color.white,
        backgroundImage: ''
      },

      'Welcome:OrderNow': {
        backgroundColor: theme.color.lighterGrey
      },
      'Welcome:Rewards': {
        backgroundColor: '#b00b69'
      }
    }
    ...
  }
 * ```
 * */

export const LayoutCard = withSpacingStyles(
  forwardRef(({ align, boxShadowDisplay = 'desktop', children, className, isHidden, mode, ...rest }: Props, ref) => {
    const componentTheme = useComponentTheme(theme => theme.components.LayoutCard, mode);

    return (
      <Section
        align={align}
        boxShadowDisplay={boxShadowDisplay}
        className={className}
        componentTheme={componentTheme}
        isHidden={isHidden}
        ref={ref}
        {...rest}
      >
        {children}
      </Section>
    );
  })
);

const Section = styled('section')(
  ({
    align: propAlign,
    boxShadowDisplay,
    componentTheme: { align: componentThemeAlign, backgroundColor, backgroundImage, border, borderRadius, padding },
    isHidden,
    theme,
  }) => {
    const align = componentThemeAlign || propAlign;
    const addStyles = (condition, styles) => (condition ? styles : '');

    const alignStyles = {
      center: 'align-items: center; text-align: center;',
      left: 'align-items: flex-start; text-align: left;',
      right: 'align-items: flex-end; text-align: right;',
    };

    const boxShadowStyle = `box-shadow: ${theme.boxShadow.small};`;

    const getDisplay = () => {
      if (isHidden) {
        return 'none';
      }

      if (align) {
        return 'flex';
      }

      return 'block';
    };

    return `
      display: ${getDisplay()};
      ${addStyles(align, `${alignStyles[align]};`)};
      ${addStyles(align, `flex-direction: column;`)};
      width: 100%;
      padding: 24px;
      border-radius: ${borderRadius || theme.borderRadius.large};
      ${addStyles(backgroundImage, `background: url(${backgroundImage}) center / cover no-repeat`)};
      ${addStyles(border, `border: ${border}`)};
      background-color: ${backgroundColor};
      ${addStyles(boxShadowDisplay === 'always', boxShadowStyle)}

      @media (${theme.breakpoint.desktop}) {
        ${addStyles(boxShadowDisplay === 'desktop', boxShadowStyle)}
        padding: ${padding};
      }
    `;
  }
);
